import {
  Calculation, CalculationRowType, TaxAtSourceUsecase, UserDefinedRow,
  BicycleBenefitUsecase, BoardUsecase, CarBenefitUsecase, DailyAllowanceUsecase, EmploymentTerminationUsecase,
  ForeclosureUsecase, HourlySalaryWithWorkingTimeCompensationUsecase, IrIncomeTypeUsecase, MealBenefitUsecase,
  NonProfitOrgUsecase, OtherCompensationUsecase, PaidSickLeaveUsecase, RemunerationUsecase, SubsidisedCommuteUsecase, TaxWithholdingUsecase, TotalWorkerPaymentUsecase,
  TrainingUsecase, UnionPaymentUsecase, WorkingTimeCompensationUsecase, AbsencePeriodUsecase,
} from "../model";

import { BaseUsecaseLogic } from "./BaseUsecaseLogic";
import { RowUsecaseCalcData } from "./RowUsecaseCalcData";
import { UsecaseRow } from "./UsecaseRow";
import { UsecaseValidationMessage } from "./UsecaseValidationMessage";

import {
  AbsencePeriodUsecaseLogic, BicycleBenefitUsecaseLogic, BoardUsecaseLogic, CarBenefitUsecaseLogic, DailyAllowanceUsecaseLogic, DefaultUsecaseLogic, EmploymentTerminationUsecaseLogic, ExpensesUsecaseLogic,
  ForeclosureUsecaseLogic, HourlySalaryWithWorkingTimeCompensationUsecaseLogic, IrIncomeTypeUsecaseLogic, MealBenefitUsecaseLogic, MilageOwnCarUsecaseLogic,
  NonProfitOrgUsecaseLogic, OtherCompensationUsecaseLogic, PaidSickLeaveUsecaseLogic, RemunerationUsecaseLogic, SubsidisedCommuteUsecaseLogic, TaxAtSourceUsecaseLogic, TaxWithholdingUsecaseLogic, TotalWorkerPaymentUsecaseLogic,
  TrainingUsecaseLogic, UnionPaymentUsecaseLogic, WorkingTimeCompensationUsecaseLogic, HolidaysUsecaseLogic
} from "./rows";
import type { ExpensesUsecase } from "./ExpensesUsecase";
import { MilageOwnCarUsecase } from "./MilageOwnCarUsecase";

/**
 * Provides logic for usecase user interfaces based on calculation rows.
 */
export class RowsUsecaseLogic {

  /**
   * Gets a blank UserDefinedRow with typed usecase data and usecase specific default data.
   * @param type Type of the usecase for the row.
   */
  public static getBlankUsecaseRow(type: CalculationRowType.AbsencePeriod): UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.BicycleBenefit): UsecaseRow<CalculationRowType.BicycleBenefit, BicycleBenefitUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Board): UsecaseRow<CalculationRowType.Board, BoardUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.CarBenefit): UsecaseRow<CalculationRowType.CarBenefit, CarBenefitUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.DailyAllowance): UsecaseRow<CalculationRowType.DailyAllowance, DailyAllowanceUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.EmploymentTermination): UsecaseRow<CalculationRowType.EmploymentTermination, EmploymentTerminationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Expenses): UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Foreclosure): UsecaseRow<CalculationRowType.Foreclosure, ForeclosureUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.HourlySalaryWithWorkingTimeCompensation): UsecaseRow<CalculationRowType.HourlySalaryWithWorkingTimeCompensation, HourlySalaryWithWorkingTimeCompensationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.IrIncomeType): UsecaseRow<CalculationRowType.IrIncomeType, IrIncomeTypeUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.MealBenefit): UsecaseRow<CalculationRowType.MealBenefit, MealBenefitUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.MilageOwnCar): UsecaseRow<CalculationRowType.MilageOwnCar, MilageOwnCarUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.NonProfitOrg): UsecaseRow<CalculationRowType.NonProfitOrg, NonProfitOrgUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.OtherCompensation): UsecaseRow<CalculationRowType.OtherCompensation, OtherCompensationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.PaidSickLeave): UsecaseRow<CalculationRowType.PaidSickLeave, PaidSickLeaveUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.PrepaidExpenses): UsecaseRow<CalculationRowType.PrepaidExpenses, ExpensesUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Remuneration): UsecaseRow<CalculationRowType.Remuneration, RemunerationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.SubsidisedCommute): UsecaseRow<CalculationRowType.SubsidisedCommute, SubsidisedCommuteUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.TotalWorkerPayment): UsecaseRow<CalculationRowType.TotalWorkerPayment, TotalWorkerPaymentUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Training): UsecaseRow<CalculationRowType.Training, TrainingUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.UnionPayment): UsecaseRow<CalculationRowType.UnionPayment, UnionPaymentUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.WorkingTimeCompensation): UsecaseRow<CalculationRowType.WorkingTimeCompensation, WorkingTimeCompensationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.TaxAtSource): UsecaseRow<CalculationRowType.TaxAtSource, TaxAtSourceUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.TaxWithholding): UsecaseRow<CalculationRowType.TaxWithholding, TaxWithholdingUsecase>;
  public static getBlankUsecaseRow<T extends CalculationRowType, D extends { [key: string]: any }>(type: CalculationRowType): UsecaseRow<T, D>;
  /**
   * Gets a blank UserDefinedRow with typed usecase data and usecase specific default data.
   * @param type Type of the usecase for the row.
   */
  public static getBlankUsecaseRow(type: CalculationRowType): UsecaseRow<CalculationRowType, any> {
    const result = {
      rowType: type,
      count: 1,
      accounting: {
        dimensions: [],
      },
      data: {},
    };
    this.getUsecaseLogic(type)?.updateUsecase(result, "today", undefined);
    return result;
  }

  /**
   * Gets the usecase specific logic for a row type.
   * @param type Row type for which we should return the logic.
   * @returns Usecase logic for the row type.
   */
  public static getUsecaseLogic(type: CalculationRowType.AbsencePeriod): AbsencePeriodUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.BicycleBenefit): BicycleBenefitUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Board): BoardUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.CarBenefit): CarBenefitUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.DailyAllowance): DailyAllowanceUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.EmploymentTermination): EmploymentTerminationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Expenses): ExpensesUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Foreclosure): ForeclosureUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.HourlySalaryWithWorkingTimeCompensation): HourlySalaryWithWorkingTimeCompensationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.IrIncomeType): IrIncomeTypeUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.MealBenefit): MealBenefitUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.MilageOwnCar): MilageOwnCarUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.NonProfitOrg): NonProfitOrgUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.OtherCompensation): OtherCompensationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.PaidSickLeave): PaidSickLeaveUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.PrepaidExpenses): ExpensesUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Remuneration): RemunerationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.SubsidisedCommute): SubsidisedCommuteUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.TotalWorkerPayment): TotalWorkerPaymentUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Training): TrainingUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.UnionPayment): UnionPaymentUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.WorkingTimeCompensation): WorkingTimeCompensationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.TaxAtSource): TaxAtSourceUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.TaxWithholding): TaxWithholdingUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.HolidayBonus | CalculationRowType.HolidaySalary | CalculationRowType.HolidayCompensation): HolidaysUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType): BaseUsecaseLogic;
  /**
   * Gets the usecase specific logic for a row type.
   * @param type Row type for which we should return the logic.
   * @returns Usecase logic for the row type.
   */
  public static getUsecaseLogic(type: CalculationRowType): BaseUsecaseLogic {
    switch (type) {
      case CalculationRowType.AbsencePeriod:
        return new AbsencePeriodUsecaseLogic();
      case CalculationRowType.BicycleBenefit:
        return new BicycleBenefitUsecaseLogic();
      case CalculationRowType.Board:
        return new BoardUsecaseLogic();
      case CalculationRowType.CarBenefit:
        return new CarBenefitUsecaseLogic();
      case CalculationRowType.DailyAllowance:
        return new DailyAllowanceUsecaseLogic();
      case CalculationRowType.EmploymentTermination:
        return new EmploymentTerminationUsecaseLogic();
      case CalculationRowType.Expenses:
        return new ExpensesUsecaseLogic();
      case CalculationRowType.Foreclosure:
        return new ForeclosureUsecaseLogic();
      case CalculationRowType.HourlySalaryWithWorkingTimeCompensation:
        return new HourlySalaryWithWorkingTimeCompensationUsecaseLogic();
      case CalculationRowType.IrIncomeType:
        return new IrIncomeTypeUsecaseLogic();
      case CalculationRowType.MealBenefit:
        return new MealBenefitUsecaseLogic();
      case CalculationRowType.MilageOwnCar:
        return new MilageOwnCarUsecaseLogic();
      case CalculationRowType.NonProfitOrg:
        return new NonProfitOrgUsecaseLogic();
      case CalculationRowType.OtherCompensation:
        return new OtherCompensationUsecaseLogic();
      case CalculationRowType.PaidSickLeave:
        return new PaidSickLeaveUsecaseLogic();
      case CalculationRowType.PrepaidExpenses:
        return new ExpensesUsecaseLogic();
      case CalculationRowType.Remuneration:
        return new RemunerationUsecaseLogic();
      case CalculationRowType.SubsidisedCommute:
        return new SubsidisedCommuteUsecaseLogic();
      case CalculationRowType.TotalWorkerPayment:
        return new TotalWorkerPaymentUsecaseLogic();
      case CalculationRowType.Training:
        return new TrainingUsecaseLogic();
      case CalculationRowType.UnionPayment:
        return new UnionPaymentUsecaseLogic();
      case CalculationRowType.WorkingTimeCompensation:
        return new WorkingTimeCompensationUsecaseLogic();
      case CalculationRowType.TaxAtSource:
        return new TaxAtSourceUsecaseLogic();
      case CalculationRowType.TaxWithholding:
        return new TaxWithholdingUsecaseLogic();
      case CalculationRowType.HolidayBonus:
      case CalculationRowType.HolidaySalary:
      case CalculationRowType.HolidayCompensation:
        return new HolidaysUsecaseLogic();
      default:
        return new DefaultUsecaseLogic();
    }
  }

  /**
   * Returns usecase logic for a row and calculation.
   * @param row Row that the logic operates on.
   * @param calc Calculation that contains the row (used for date, potentially other things).
   * @param doUpdate If true, will run first updateUsecase on the row.
   */
  public static getUsecaseLogicForCalc(row: UserDefinedRow, calc: Calculation, doUpdate = false): BaseUsecaseLogic {
    const logic = RowsUsecaseLogic.getUsecaseLogic(row.rowType);
    if (doUpdate) {
      logic.updateUsecase(row, calc?.workflow?.salaryDate ?? "today", RowUsecaseCalcData.fromCalc(calc));
    }
    return logic;
  }

  /**
   * Returns a validation message for a row or null if none is provided.
   * Does the common validations (price and count) and then runs usecase specific validations.
   * @param row The row that is validated.
   */
  public static getValidation(row: UserDefinedRow): UsecaseValidationMessage {
    return RowsUsecaseLogic.getUsecaseLogic(row.rowType).getValidation(row);
  }
}
